import { CreditCard, Plus } from "lucide-react";
import { Link, type useNavigate } from "react-router-dom";

interface Payment {
	id: string;
	name: string;
	amount: number;
	status: string;
	createdAt: string;
	failureReason: string | null;
}

interface RecentPaymentsProps {
	recentPayments: Payment[];
	navigate: ReturnType<typeof useNavigate>;
	onNextPage: () => void;
	onPreviousPage: () => void;
	currentPage: number;
	hasNextPage: boolean;
	isPaginating: boolean;
}

export default function RecentPaymentsTable({
	recentPayments,
	navigate,
	onNextPage,
	onPreviousPage,
	currentPage,
	hasNextPage,
	isPaginating,
}: RecentPaymentsProps) {
	return (
		<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
			<h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Payments</h2>
			{recentPayments.length === 0 ? (
				<div className="text-center py-8">
					<CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
					<h3 className="text-lg font-medium text-gray-900 mb-2">No payments yet</h3>
					<p className="text-gray-500 mb-4">
						No Payments yet. Create your first payment to get started.
					</p>
					<Link
						to="/payments/new"
						className="group bg-gradient-to-r from-orange-400 to-pink-400 hover:from-orange-500 hover:to-pink-500 text-white font-semibold px-4 py-2 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center gap-2 mt-3 w-44 md:mt-0 md:max-w-44 mx-auto"
					>
						<Plus className="w-5 h-5" />
						New Payment
					</Link>
				</div>
			) : (
				<>
					<div className="relative">
						{isPaginating && (
							<div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-lg flex items-center justify-center z-10">
								<div className="flex items-center gap-2 text-gray-600">
									<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900" />
									<span className="text-sm font-medium">Loading...</span>
								</div>
							</div>
						)}
						<div className="space-y-3">
							{recentPayments
								.filter((payment) => payment.status !== "expired")
								.map((payment) => (
									<div
										onClick={() => navigate(`/payments/${payment.id}`)}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												navigate(`/payments/${payment.id}`);
											}
										}}
										key={payment.id}
										// biome-ignore lint/a11y/useSemanticElements: div with role="button" is appropriate for this clickable row layout
										role="button"
										tabIndex={0}
										className={`flex items-center justify-between p-3 ${payment.status === "failed" ? "bg-red-50" : "bg-gray-50"} rounded-lg cursor-pointer hover:bg-gray-100 transition-colors`}
									>
										<div>
											<h3 className="font-medium text-gray-900">{payment.name}</h3>
											<p className="text-sm text-gray-500">
												Created: {new Date(payment.createdAt).toLocaleDateString("en-GB")}
											</p>
										</div>
										<div className="text-right">
											<p className="font-semibold text-gray-900">
												£{(payment.amount / 100).toFixed(2)}
											</p>
											<button
												// disabled={payment.status === "failed"}
												type="button"
												onClick={() => navigate(`/payments/${payment.id}`)}
												className="text-sm text-right text-orange-600 hover:text-orange-700"
											>
												{payment.status === "failed" ? payment.failureReason : payment.status}
											</button>
										</div>
									</div>
								))}
						</div>
					</div>

					{/* Pagination Controls */}
					<div className="flex items-center justify-between mt-6">
						<span className="text-sm text-gray-500">Page {currentPage}</span>
						<div className="flex items-center space-x-4">
							<button
								type="button"
								className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
								disabled={currentPage <= 1 || isPaginating}
								title="Previous"
								onClick={onPreviousPage}
							>
								<span>
									{isPaginating && currentPage > 1 ? (
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700" />
									) : (
										<span>Previous</span>
									)}
								</span>
							</button>

							<button
								type="button"
								className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
								disabled={!hasNextPage || isPaginating}
								title="Next"
								onClick={onNextPage}
							>
								<span>
									{isPaginating && hasNextPage ? (
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700" />
									) : (
										<span>Next</span>
									)}
								</span>
							</button>
						</div>
					</div>
				</>
			)}
		</div>
	);
}
